import {
  confirm,
  dismissConfirm,
  IConfirm,
} from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import React from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import {
  installLeaveGuard,
  ITraversalNavigation,
  TAskToLeave,
  TWithdrawQuestion,
} from "./leaveGuard.utils";

// Asks in the confirm dialog whether to leave; returns how the guard takes
// the dialog back once the question no longer applies.
export const confirmLeave = (params: {
  title: string;
  info: string;
  leave: () => void;
}): TWithdrawQuestion => {
  const { title, info, leave } = params;
  const dialog: IConfirm = {
    type: "warning",
    title,
    info,
    primaryActions: [
      {
        title: getLocalizedText("settlement.leave.confirm"),
        onPress: leave,
      },
    ],
  };
  confirm(dialog);
  return () => dismissConfirm(dialog);
};

const askToLeaveSettlement: TAskToLeave = leave =>
  confirmLeave({
    title: getLocalizedText("settlement.leave.title"),
    info: getLocalizedText("settlement.leave.info"),
    leave,
  });

// While `when` holds, leaving the page asks first: reload, closing the tab,
// browser back or forward, a sidebar item, the breadcrumb or any other link.
// `isLeaveAllowed` is checked at the moment of leaving, so a save that just
// finished can redirect before the page re-renders.
// INFO: the dashboard runs on BrowserRouter, which has no useBlocker; the
// guard wraps its navigator instead (see installLeaveGuard).
export const useLeaveGuard = (params: {
  when: boolean;
  isLeaveAllowed: () => boolean;
}) => {
  const { when, isLeaveAllowed } = params;
  const { navigator } = React.useContext(UNSAFE_NavigationContext);

  // Lifecycle
  const isLeaveAllowedRef = React.useRef(isLeaveAllowed);
  React.useEffect(() => {
    isLeaveAllowedRef.current = isLeaveAllowed;
  });

  React.useEffect(() => {
    if (!when) return undefined;
    const { navigation } = window as Window & {
      navigation?: ITraversalNavigation;
    };
    return installLeaveGuard({
      target: window,
      location: window.location,
      navigator,
      navigation,
      shouldAsk: () => !isLeaveAllowedRef.current(),
      askToLeave: askToLeaveSettlement,
    });
  }, [when, navigator]);
};
